import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Navbar, Footer } from "../components/Layout";
import { PropertyCard, imgUrl } from "../components/PropertyCard";
import { VideoPlayer } from "../components/VideoPlayer";
import { useText } from "../contexts/TextsContext";
import { ArrowRight, Layers, ShieldCheck, Truck, Zap, ChevronRight } from "lucide-react";
import { Helmet } from "../components/Helmet";

export default function Landing() {
  const [properties, setProperties] = useState([]);
  const [hero, setHero] = useState(null);
  const t = useText();

  useEffect(() => {
    api.get("/properties").then(r => setProperties(r.data)).catch(() => {});
    api.get("/site-content/hero").then(r => setHero(r.data.value)).catch(() => {});
  }, []);

  const h = hero || {};
  const isVideo = h.media_type === "video";
  const videoEmbed = isVideo && h.media_url ? { kind: /youtu/.test(h.media_url) ? "youtube" : /vimeo/.test(h.media_url) ? "vimeo" : "upload", url: h.media_url } : null;

  const features = [
    { icon: Layers, t: t("landing.features.0.title"), d: t("landing.features.0.desc") },
    { icon: Truck, t: t("landing.features.1.title"), d: t("landing.features.1.desc") },
    { icon: Zap, t: t("landing.features.2.title"), d: t("landing.features.2.desc") },
    { icon: ShieldCheck, t: t("landing.features.3.title"), d: t("landing.features.3.desc") },
  ];
  const stats = [
    { v: t("landing.stats.0.value") || properties.length.toString(), l: t("landing.stats.0.label") },
    { v: t("landing.stats.1.value"), l: t("landing.stats.1.label") },
    { v: t("landing.stats.2.value"), l: t("landing.stats.2.label") },
    { v: t("landing.stats.3.value"), l: t("landing.stats.3.label") },
  ];

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      <Helmet
        title="Immobilier Solaire — Local d'activité & hangar photovoltaïque à Cercottes (Loiret)"
        description="Louez votre local d'activité industriel, hangar photovoltaïque ou entrepôt solaire à Cercottes, Loiret. Bâtiments modulables 270 à 800 m², autoconsommation, à 25 min d'Orléans."
        path="/"
      />
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          {isVideo && videoEmbed ? (
            <div className="w-full h-full">
              {videoEmbed.kind === "upload" ? (
                <video src={imgUrl({ url: h.media_url })} className="w-full h-full object-cover" autoPlay muted loop playsInline />
              ) : (
                <VideoPlayer video={videoEmbed} className="w-full h-full" autoplay muted loop controls={false} />
              )}
            </div>
          ) : (
            h.media_url && <img src={h.media_url} alt="" className="w-full h-full object-cover" />
          )}
          <div className="absolute inset-0 bg-slate-900/60"/>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-8 py-32 md:py-48">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-[#FFC107] text-slate-900 px-4 py-2 mb-8 border border-slate-900">
              <div className="w-2 h-2 bg-slate-900 rounded-full animate-pulse" />
              <span className="text-xs font-bold uppercase tracking-widest">{h.kicker || "Cercottes — 45520 Loiret"}</span>
            </div>
            <h1 className="font-display font-black text-5xl md:text-7xl lg:text-8xl text-white leading-[0.9] tracking-tight mb-8">
              {h.title_line1 || "Des locaux"}<br/>{h.title_line2 || "industriels"}<br/><span className="text-[#FFC107]">{h.title_accent || "modulables."}</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-200 max-w-xl leading-relaxed mb-12">
              {h.subtitle || "270 à 450 m² de bâtiments d'activité prêts à accueillir votre PME, atelier ou plateforme logistique."}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to={h.cta_primary_link || "/locaux"} data-testid="hero-cta-primary"
                className="btn-brutal btn-brutal-solar px-8 py-4 font-bold uppercase tracking-wider text-sm inline-flex items-center gap-3">
                {h.cta_primary_label || "Voir les locaux"} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to={h.cta_secondary_link || "/demande"} data-testid="hero-cta-secondary"
                className="btn-brutal bg-white border border-slate-900 text-slate-900 px-8 py-4 font-bold uppercase tracking-wider text-sm">
                {h.cta_secondary_label || "Demande sur mesure"}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* STATS BAR */}
      <section className="border-y border-slate-900 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8 grid grid-cols-2 md:grid-cols-4 divide-x divide-slate-900">
          {stats.map((s) => (
            <div key={s.l} className="p-8 text-center">
              <div className="font-display font-black text-3xl md:text-5xl tracking-tight">{s.v}</div>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mt-2">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-6 md:px-8 py-24 md:py-32" data-testid="features-section">
        <div className="grid md:grid-cols-12 gap-12 mb-16">
          <div className="md:col-span-5">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FFC107] mb-4">{t("landing.features.kicker")}</div>
            <h2 className="font-display font-black text-4xl md:text-5xl leading-tight tracking-tight whitespace-pre-line">
              {t("landing.features.title")}
            </h2>
          </div>
          <p className="md:col-span-7 text-lg leading-relaxed text-slate-600 md:pt-12">
            {t("landing.features.intro")}
          </p>
        </div>

        <div className="grid md:grid-cols-4 gap-6">
          {features.map((f) => (
            <div key={f.t} className="bg-white border border-slate-900/15 p-8 card-hover">
              <f.icon className="w-8 h-8 text-[#FFC107]" strokeWidth={2} />
              <div className="font-display font-bold text-xl mt-6 mb-2">{f.t}</div>
              <p className="text-sm text-slate-600 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PROPERTIES */}
      <section className="bg-white border-y border-slate-900/10">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-24 md:py-32">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FFC107] mb-4">{t("landing.properties.kicker")}</div>
              <h2 className="font-display font-black text-4xl md:text-5xl tracking-tight">{t("landing.properties.title")}</h2>
            </div>
            <Link to="/locaux" data-testid="see-all-properties" className="text-sm font-bold uppercase tracking-wider inline-flex items-center gap-2 border-b-2 border-slate-900 pb-1">
              {t("landing.properties.see_all")} <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {properties.slice(0,3).map(p => <PropertyCard key={p.id} property={p} />)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-24 md:py-32 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <h2 className="font-display font-black text-4xl md:text-6xl tracking-tight leading-[0.95]">
              {t("landing.cta.title_line1")}<br/><span className="text-[#FFC107]">{t("landing.cta.title_line2")}</span>
            </h2>
            <p className="text-slate-300 mt-6 max-w-xl leading-relaxed">
              {t("landing.cta.desc")}
            </p>
          </div>
          <div className="md:col-span-5 flex md:justify-end">
            <Link to="/demande" data-testid="cta-demande-bottom" className="btn-brutal btn-brutal-solar px-10 py-5 font-bold uppercase tracking-wider inline-flex items-center gap-3">
              {t("global.cta_request")} <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
